import { businessDateTimeValue, formatSriLankaDate, sriLankaDateParts } from "./sriLankaTime";

/**
 * Business-day helpers.
 *
 * Rules:
 *  - A bar business day starts at 03:00 AM of the chosen date.
 *  - It ends at 02:10 AM of the NEXT calendar date.
 *  - Any sale that happens in that window belongs to the chosen business date.
 */

const pad = (n) => String(n).padStart(2, "0");

/** Returns the ISO date (YYYY-MM-DD) representing the CURRENT business day. */
export const currentBusinessDate = (now = new Date()) => {
  const parts = sriLankaDateParts(now);
  if (!parts) return "";
  const today = `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
  return parts.hour < 3 ? addDays(today, -1) : today;
};

/** Returns the next ISO date (YYYY-MM-DD) after the given business date. */
export const addDays = (yyyymmdd, n) => {
  const [year, month, day] = yyyymmdd.split("-").map(Number);
  const d = new Date(Date.UTC(year, month - 1, day + n));
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

/** Returns the {fromDateTime, toDateTime} for a SINGLE business date. */
export const businessDayRange = (yyyymmdd) => ({
  fromDateTime: `${yyyymmdd}T03:00:00`,
  toDateTime: `${addDays(yyyymmdd, 1)}T02:10:00`,
});

/** Returns the {fromDateTime, toDateTime} for an inclusive business-date range. */
export const businessRange = (fromYyyymmdd, toYyyymmdd) => ({
  fromDateTime: `${fromYyyymmdd}T03:00:00`,
  toDateTime: `${addDays(toYyyymmdd, 1)}T02:10:00`,
});

/** Returns true if the given ISO timestamp falls inside the business-day range. */
export const isInBusinessDay = (iso, yyyymmdd) => {
  if (!iso) return false;

  const { fromDateTime, toDateTime } = businessDayRange(yyyymmdd);
  const ts = businessDateTimeValue(iso);
  return ts >= businessDateTimeValue(fromDateTime) && ts < businessDateTimeValue(toDateTime);
};

/**
 * Whether a cashier can open a NEW session for `yyyymmdd` "right now".
 */
export const canOpenSessionFor = (yyyymmdd, now = new Date()) =>
  currentBusinessDate(now) >= yyyymmdd;

/** Pretty label for a single business date. */
export const businessDateLabel = (yyyymmdd) => {
  return formatSriLankaDate(yyyymmdd, { day: "2-digit", month: "short", year: "numeric" });
};

export const isSalesBlockedNow = (now = new Date()) => {
  const parts = sriLankaDateParts(now);
  if (!parts) return false;
  const minutes = parts.hour * 60 + parts.minute;

  // Block sales from 02:10 AM until 03:00 AM
  return minutes >= 130 && minutes < 180;
};
